import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { PaymentForm, CreditCard, GooglePay, ApplePay } from 'react-square-web-payments-sdk';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Star, Check, Gift, Truck, MapPin, Package, Clock, Calendar, AlertCircle, Shield, CreditCard as CreditCardIcon, Banknote } from 'lucide-react';
import useCart from '@/store/cartStore';
import useUserStore from '@/store/userStore';
import { toast } from 'sonner';
import { LiveArrivalBadge, EstimatedDelivery } from '@/components/ShippingFeatures';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APP_ID;
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;

const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, sessionId, getTotal, clearCart } = useCart();
  const { user, isLoggedIn, refreshProfile } = useUserStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [redemptionCount, setRedemptionCount] = useState(0);
  // Initialize delivery method from URL param or default to 'shipping'
  const [deliveryMethod, setDeliveryMethod] = useState(searchParams.get('delivery') || 'shipping');
  const [pickupAvailability, setPickupAvailability] = useState([]);
  const [selectedPickupDate, setSelectedPickupDate] = useState('');
  const [selectedPickupTime, setSelectedPickupTime] = useState('');
  const [loadingPickup, setLoadingPickup] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'cash'
  
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm({
    defaultValues: {
      country: 'US'
    }
  });

  // Apply promo code
  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const response = await axios.post(`${API}/promo/validate`, {
        code: promoCode.trim().toUpperCase(),
        user_email: user?.email || null
      });
      if (response.data.valid) {
        setPromoApplied({
          code: promoCode.trim().toUpperCase(),
          discount_percent: response.data.discount_percent,
          discount_amount: subtotal * (response.data.discount_percent / 100)
        });
        toast.success(`Promo code applied! ${response.data.discount_percent}% off`);
      } else {
        toast.error(response.data.message || 'Invalid promo code');
        setPromoApplied(null);
      }
    } catch (error) {
      toast.error('Invalid or expired promo code');
      setPromoApplied(null);
    }
    setPromoLoading(false);
  };

  const removePromoCode = () => {
    setPromoCode('');
    setPromoApplied(null);
  };

  // Fetch pickup availability
  const fetchPickupAvailability = useCallback(async () => {
    setLoadingPickup(true);
    try {
      const response = await axios.get(`${API}/pickup/availability?days_ahead=14`);
      if (response.data.success) {
        setPickupAvailability(response.data.availability);
      }
    } catch (error) {
      console.error('Error fetching pickup availability:', error);
      toast.error('Failed to load pickup times');
    } finally {
      setLoadingPickup(false);
    }
  }, []);

  useEffect(() => {
    if (deliveryMethod === 'local_pickup') {
      fetchPickupAvailability();
    }
  }, [deliveryMethod, fetchPickupAvailability]);

  useEffect(() => {
    if (isLoggedIn && user?.email) {
      setValue('email', user.email);
    }
  }, [isLoggedIn, user, setValue]);

  // Reset pickup selection when date changes
  useEffect(() => {
    setSelectedPickupTime('');
  }, [selectedPickupDate]);

  const subtotal = getTotal();
  
  // Calculate shipping based on delivery method and subtotal
  const calculateShipping = () => {
    if (deliveryMethod === 'local_pickup') return 0;
    if (subtotal >= 60) return 0;
    return 9.99;
  };
  
  const shipping = calculateShipping();
  const tax = subtotal * 0.11;
  
  // Calculate max redemptions available (each 100 points = $5)
  const maxRedemptions = isLoggedIn ? Math.floor((user?.loyalty_points || 0) / 100) : 0;
  const pointsDiscount = redemptionCount * 5;
  const pointsToRedeem = redemptionCount * 100;
  
  const total = Math.max(0, subtotal + shipping + tax - pointsDiscount);
  // Points calculation: 2 points per dollar, rounded UP
  const pointsToEarn = Math.ceil(subtotal * 2);
  const canRedeemPoints = isLoggedIn && (user?.loyalty_points || 0) >= 100;

  // Get available time slots for selected date
  const getAvailableTimeSlots = () => {
    const dateData = pickupAvailability.find(d => d.date === selectedPickupDate);
    return dateData?.slots || [];
  };

  // Handle Square payment token
  const handleCardTokenization = async (token, formData) => {
    setIsProcessing(true);
    try {
      // Validate pickup selection for local pickup
      if (deliveryMethod === 'local_pickup') {
        if (!selectedPickupDate || !selectedPickupTime) {
          toast.error('Please select a pickup date and time');
          setIsProcessing(false);
          return;
        }
      }

      const checkoutData = {
        source_id: token.token,
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        street_address: deliveryMethod === 'shipping' ? formData.streetAddress : null,
        city: deliveryMethod === 'shipping' ? formData.city : null,
        state: deliveryMethod === 'shipping' ? formData.state : null,
        postal_code: deliveryMethod === 'shipping' ? formData.postalCode : null,
        country: deliveryMethod === 'shipping' ? (formData.country || 'US') : null,
        session_id: sessionId,
        redeem_points: pointsToRedeem,
        user_email: isLoggedIn ? user?.email : null,
        promo_code: promoApplied?.code || null,
        delivery_method: deliveryMethod,
        pickup_date: deliveryMethod === 'local_pickup' ? selectedPickupDate : null,
        pickup_time: deliveryMethod === 'local_pickup' ? selectedPickupTime : null
      };

      const response = await axios.post(`${API}/checkout`, checkoutData);
      
      if (response.data.success) {
        clearCart();
        
        if (isLoggedIn) {
          await refreshProfile();
        }
        
        // Only show points message if user is logged in
        const earnedMsg = (isLoggedIn && response.data.points_earned > 0)
          ? ` You earned ${response.data.points_earned} points!` 
          : '';
        toast.success(`Order placed successfully!${earnedMsg}`);
        navigate(`/order-confirmation/${response.data.order_id}`);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to process order';
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle Cash at Pickup orders (no card payment required)
  const handleCashAtPickupOrder = async () => {
    setIsProcessing(true);
    try {
      // Validate pickup selection
      if (!selectedPickupDate || !selectedPickupTime) {
        toast.error('Please select a pickup date and time');
        setIsProcessing(false);
        return;
      }

      const formData = watch();
      
      // Validate required fields
      if (!formData.email || !formData.firstName || !formData.lastName || !formData.phone) {
        toast.error('Please fill in all required contact information');
        setIsProcessing(false);
        return;
      }

      const checkoutData = {
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        session_id: sessionId,
        redeem_points: pointsToRedeem,
        user_email: isLoggedIn ? user?.email : null,
        promo_code: promoApplied?.code || null,
        delivery_method: 'local_pickup',
        pickup_date: selectedPickupDate,
        pickup_time: selectedPickupTime,
        payment_method: 'cash_at_pickup'
      };

      const response = await axios.post(`${API}/checkout/cash`, checkoutData);
      
      if (response.data.success) {
        clearCart();
        
        if (isLoggedIn) {
          await refreshProfile();
        }
        
        const earnedMsg = (isLoggedIn && response.data.points_earned > 0)
          ? ` You'll earn ${response.data.points_earned} points after pickup!` 
          : '';
        toast.success(`Order reserved!${earnedMsg} Pay at pickup.`);
        navigate(`/order-confirmation/${response.data.order_id}`);
      }
    } catch (error) {
      console.error('Cash order error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to place order';
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  // Wrapper to get form data for Square payment
  const onFormSubmit = (data) => {
    // Store form data for use in card tokenization
    window._checkoutFormData = data;
  };

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen py-12 md:py-16" data-testid="checkout-page">
      <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-5xl">
        <h1 className="heading-font text-4xl sm:text-5xl md:text-6xl font-bold mb-8 md:mb-12" data-testid="checkout-title">Checkout</h1>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6 md:space-y-8">
          {/* Rewards Status Bar */}
          {isLoggedIn && (
            <Card className="p-4 md:p-6 bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200" data-testid="rewards-status">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <span className="text-lg">🌿</span>
                  </div>
                  <div>
                    <p className="font-semibold text-amber-800">
                      You have {user?.loyalty_points || 0} Plant Perks
                      {maxRedemptions > 0 && <span className="text-green-600"> (${maxRedemptions * 5} value)</span>}
                    </p>
                    <p className="text-sm text-amber-600">Earn {pointsToEarn} more perks with this order</p>
                  </div>
                </div>
                {canRedeemPoints && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setRedemptionCount(Math.max(0, redemptionCount - 1))}
                      disabled={redemptionCount === 0}
                      className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-50 flex items-center justify-center font-bold"
                    >
                      -
                    </button>
                    <div className={`px-4 py-2 rounded-full ${redemptionCount > 0 ? 'bg-green-600 text-white' : 'bg-white border border-amber-300 text-amber-700'}`}>
                      {redemptionCount > 0 ? `$${pointsDiscount} off` : 'Use perks?'}
                    </div>
                    <button
                      type="button"
                      onClick={() => setRedemptionCount(Math.min(maxRedemptions, redemptionCount + 1))}
                      disabled={redemptionCount >= maxRedemptions}
                      className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-50 flex items-center justify-center font-bold"
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Guest Prompt */}
          {!isLoggedIn && (
            <Card className="p-4 md:p-6 bg-gradient-to-r from-primary/5 to-green-50 border-primary/20">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="font-semibold">Not a member yet?</p>
                  <p className="text-sm text-muted-foreground">Sign up to earn {pointsToEarn} Plant Perks on this order!</p>
                </div>
                <p className="text-xs text-muted-foreground">Create account after checkout to save perks</p>
              </div>
            </Card>
          )}

          {/* Delivery Method Selection */}
          <Card className="p-5 md:p-8" data-testid="delivery-method-card">
            <h2 className="heading-font text-xl md:text-2xl font-semibold mb-4 md:mb-6">Delivery Method</h2>
            
            <RadioGroup 
              value={deliveryMethod} 
              onValueChange={setDeliveryMethod}
              className="space-y-4"
            >
              {/* Shipping Option */}
              <div className={`relative flex items-start p-4 rounded-lg border-2 transition-all cursor-pointer ${
                deliveryMethod === 'shipping' 
                  ? 'border-primary bg-primary/5' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <RadioGroupItem value="shipping" id="shipping" className="mt-1" />
                <label htmlFor="shipping" className="flex-1 ml-3 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Ship to Address</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    USPS Priority 2-Day • {subtotal >= 60 ? (
                      <span className="text-green-600 font-medium">FREE</span>
                    ) : (
                      '$9.99'
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ships Mon-Wed for same-week delivery
                  </p>
                  {subtotal < 60 && (
                    <p className="text-xs text-primary mt-1">
                      Add ${(60 - subtotal).toFixed(2)} more for free shipping!
                    </p>
                  )}
                </label>
              </div>

              {/* Local Pickup Option */}
              <div className={`relative flex items-start p-4 rounded-lg border-2 transition-all cursor-pointer ${
                deliveryMethod === 'local_pickup' 
                  ? 'border-primary bg-primary/5' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <RadioGroupItem value="local_pickup" id="local_pickup" className="mt-1" />
                <label htmlFor="local_pickup" className="flex-1 ml-3 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Local Pickup</span>
                    <span className="text-green-600 font-medium text-sm">FREE</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Springfield, MO
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Sat-Sun: 10am-7pm • Mon-Fri: 5pm-8pm
                  </p>
                </label>
              </div>
            </RadioGroup>
          </Card>

          {/* Pickup Scheduler - Only shows for local pickup */}
          {deliveryMethod === 'local_pickup' && (
            <Card className="p-5 md:p-8" data-testid="pickup-scheduler-card">
              <h2 className="heading-font text-xl md:text-2xl font-semibold mb-4 md:mb-6 flex items-center gap-2">
                <Calendar className="w-6 h-6 text-primary" />
                Schedule Your Pickup
              </h2>
              
              {loadingPickup ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <span className="ml-3 text-muted-foreground">Loading available times...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Date Selection */}
                  <div>
                    <Label className="text-base font-medium mb-3 block">Select Pickup Date</Label>
                    <Select value={selectedPickupDate} onValueChange={setSelectedPickupDate}>
                      <SelectTrigger className="w-full" data-testid="pickup-date-select">
                        <SelectValue placeholder="Choose a date..." />
                      </SelectTrigger>
                      <SelectContent>
                        {pickupAvailability.map((dateOption) => (
                          <SelectItem key={dateOption.date} value={dateOption.date}>
                            {dateOption.display} ({dateOption.slots.length} slots available)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Time Selection */}
                  {selectedPickupDate && (
                    <div>
                      <Label className="text-base font-medium mb-3 block">Select Pickup Time</Label>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                        {getAvailableTimeSlots().map((slot) => (
                          <button
                            key={slot.value}
                            type="button"
                            onClick={() => setSelectedPickupTime(slot.value)}
                            className={`p-2 text-sm rounded-md border transition-all ${
                              selectedPickupTime === slot.value
                                ? 'border-primary bg-primary text-white'
                                : 'border-gray-200 hover:border-primary hover:bg-primary/5'
                            }`}
                            data-testid={`pickup-time-${slot.value}`}
                          >
                            <Clock className="w-3 h-3 inline mr-1" />
                            {slot.display}
                            {slot.spots_left === 1 && (
                              <span className="block text-xs text-amber-500">1 spot left</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Selected pickup summary */}
                  {selectedPickupDate && selectedPickupTime && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-green-800">Pickup Scheduled</p>
                        <p className="text-sm text-green-700">
                          {pickupAvailability.find(d => d.date === selectedPickupDate)?.display} at{' '}
                          {getAvailableTimeSlots().find(s => s.value === selectedPickupTime)?.display}
                        </p>
                        <p className="text-xs text-green-600 mt-1">Springfield, MO</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Contact & Shipping Info */}
          <Card className="p-5 md:p-8">
            <h2 className="heading-font text-xl md:text-2xl font-semibold mb-4 md:mb-6">
              {deliveryMethod === 'local_pickup' ? 'Contact Information' : 'Shipping Information'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  {...register('firstName', { required: 'First name is required' })}
                  className="mt-2"
                  data-testid="first-name-input"
                />
                {errors.firstName && <p className="text-destructive text-sm mt-1">{errors.firstName.message}</p>}
              </div>
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  {...register('lastName', { required: 'Last name is required' })}
                  className="mt-2"
                  data-testid="last-name-input"
                />
                {errors.lastName && <p className="text-destructive text-sm mt-1">{errors.lastName.message}</p>}
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email', { required: 'Email is required', pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email' } })}
                  className="mt-2"
                  data-testid="email-input"
                />
                {errors.email && <p className="text-destructive text-sm mt-1">{errors.email.message}</p>}
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  {...register('phone', { required: 'Phone is required' })}
                  className="mt-2"
                  data-testid="phone-input"
                />
                {errors.phone && <p className="text-destructive text-sm mt-1">{errors.phone.message}</p>}
              </div>

              {/* Shipping Address Fields - Only show for shipping */}
              {deliveryMethod === 'shipping' && (
                <>
                  <div className="md:col-span-2">
                    <Label htmlFor="streetAddress">Street Address *</Label>
                    <Input
                      id="streetAddress"
                      {...register('streetAddress', { 
                        required: deliveryMethod === 'shipping' ? 'Street address is required' : false 
                      })}
                      className="mt-2"
                      data-testid="street-input"
                    />
                    {errors.streetAddress && <p className="text-destructive text-sm mt-1">{errors.streetAddress.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      {...register('city', { 
                        required: deliveryMethod === 'shipping' ? 'City is required' : false 
                      })}
                      className="mt-2"
                      data-testid="city-input"
                    />
                    {errors.city && <p className="text-destructive text-sm mt-1">{errors.city.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      {...register('state', { 
                        required: deliveryMethod === 'shipping' ? 'State is required' : false 
                      })}
                      className="mt-2"
                      data-testid="state-input"
                    />
                    {errors.state && <p className="text-destructive text-sm mt-1">{errors.state.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="postalCode">Postal Code *</Label>
                    <Input
                      id="postalCode"
                      {...register('postalCode', { 
                        required: deliveryMethod === 'shipping' ? 'Postal code is required' : false 
                      })}
                      className="mt-2"
                      data-testid="postal-input"
                    />
                    {errors.postalCode && <p className="text-destructive text-sm mt-1">{errors.postalCode.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      defaultValue="US"
                      {...register('country')}
                      className="mt-2"
                      data-testid="country-input"
                    />
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Order Summary */}
          <Card className="p-5 md:p-8">
            <h2 className="heading-font text-xl md:text-2xl font-semibold mb-4 md:mb-6">Order Summary</h2>
            <div className="space-y-3 md:space-y-4 mb-6">
              {cart.map((item) => (
                <div key={item.product_id} className="flex justify-between text-sm md:text-base" data-testid={`summary-item-${item.product_id}`}>
                  <span className="flex-1">{item.name} x {item.quantity}</span>
                  <span className="ml-2">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span data-testid="summary-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    {deliveryMethod === 'local_pickup' ? (
                      <><MapPin className="w-4 h-4" /> Local Pickup</>
                    ) : (
                      <><Truck className="w-4 h-4" /> Shipping</>
                    )}
                  </span>
                  <span data-testid="summary-shipping" className={shipping === 0 ? 'text-green-600 font-medium' : ''}>
                    {shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tax</span>
                  <span data-testid="summary-tax">${tax.toFixed(2)}</span>
                </div>
                {redemptionCount > 0 && pointsDiscount > 0 && (
                  <div className="flex justify-between text-green-600" data-testid="points-discount">
                    <span className="flex items-center gap-1">
                      🌿 Plant Perks ({pointsToRedeem} pts)
                    </span>
                    <span>-${pointsDiscount.toFixed(2)}</span>
                  </div>
                )}
                {promoApplied && (
                  <div className="flex justify-between text-green-600" data-testid="promo-discount">
                    <span className="flex items-center gap-1">
                      <Gift className="w-4 h-4" /> Promo ({promoApplied.code})
                      <button 
                        onClick={removePromoCode} 
                        className="text-red-500 hover:text-red-700 text-xs ml-1"
                      >
                        ✕
                      </button>
                    </span>
                    <span>-${promoApplied.discount_amount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t pt-4">
                  <span>Total</span>
                  <span className="text-primary" data-testid="summary-total">
                    ${(total - (promoApplied?.discount_amount || 0)).toFixed(2)}
                  </span>
                </div>
                {isLoggedIn && pointsToEarn > 0 && (
                  <p className="text-sm text-green-600 text-right">
                    +{pointsToEarn} Plant Perks after purchase
                  </p>
                )}
              </div>
              
              {/* Promo Code Input */}
              <div className="border-t pt-4 mt-4">
                <Label className="text-sm font-medium mb-2 block">Have a promo code?</Label>
                
                {/* Show user's welcome code if they have one and haven't used it */}
                {isLoggedIn && user?.has_first_order_discount && user?.discount_code && !promoApplied && (
                  <div className="mb-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg" data-testid="welcome-promo-banner">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Gift className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="text-sm font-semibold text-green-800">Your 15% Welcome Discount!</p>
                          <p className="text-xs text-green-600">First-time buyer exclusive</p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => {
                          setPromoCode(user.discount_code);
                          // Auto-apply the code
                          setTimeout(() => applyPromoCode(), 100);
                        }}
                        data-testid="apply-welcome-promo-btn"
                      >
                        Apply {user.discount_code}
                      </Button>
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    disabled={promoApplied !== null}
                    className="flex-1 uppercase"
                    data-testid="promo-code-input"
                  />
                  {promoApplied ? (
                    <Button 
                      type="button"
                      variant="outline" 
                      onClick={removePromoCode}
                      data-testid="remove-promo-btn"
                    >
                      Remove
                    </Button>
                  ) : (
                    <Button 
                      type="button"
                      variant="outline" 
                      onClick={applyPromoCode}
                      disabled={promoLoading || !promoCode.trim()}
                      data-testid="apply-promo-btn"
                    >
                      {promoLoading ? 'Checking...' : 'Apply'}
                    </Button>
                  )}
                </div>
                {!isLoggedIn && (
                  <p className="text-xs text-muted-foreground mt-2">
                    <Gift className="w-3 h-3 inline mr-1" />
                    New customer? Sign up for 15% off your first order!
                  </p>
                )}
              </div>
            </div>

            {/* Square Payment Form */}
            <div className="mt-6" data-testid="payment-form">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Payment Details
              </h3>
              
              {deliveryMethod === 'local_pickup' && (!selectedPickupDate || !selectedPickupTime) && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800">
                  <AlertCircle className="w-5 h-5" />
                  <span className="text-sm">Please select a pickup date and time above before paying</span>
                </div>
              )}
              
              {/* Live Arrival Guarantee */}
              <div className="mb-4 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                <Shield className="w-4 h-4 flex-shrink-0" />
                <span>100% Live Arrival Guarantee on all orders</span>
              </div>
              
              {/* Estimated Delivery */}
              {deliveryMethod === 'shipping' && (
                <div className="mb-4">
                  <EstimatedDelivery />
                </div>
              )}

              {/* Payment Method Selection for Local Pickup */}
              {deliveryMethod === 'local_pickup' && (
                <div className="mb-4" data-testid="payment-method-selection">
                  <Label className="text-sm font-medium mb-3 block">Payment Method</Label>
                  <RadioGroup 
                    value={paymentMethod} 
                    onValueChange={setPaymentMethod}
                    className="grid grid-cols-2 gap-3"
                  >
                    <div className={`flex items-center p-3 rounded-lg border-2 transition-all cursor-pointer ${
                      paymentMethod === 'card' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <RadioGroupItem value="card" id="payment-card" />
                      <label htmlFor="payment-card" className="flex-1 ml-3 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <CreditCardIcon className="w-4 h-4 text-primary" />
                          <span className="font-medium text-sm">Pay Now</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Card, Apple Pay, Google Pay
                        </p>
                      </label>
                    </div>
                    
                    <div className={`flex items-center p-3 rounded-lg border-2 transition-all cursor-pointer ${
                      paymentMethod === 'cash' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <RadioGroupItem value="cash" id="payment-cash" />
                      <label htmlFor="payment-cash" className="flex-1 ml-3 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <Banknote className="w-4 h-4 text-green-600" />
                          <span className="font-medium text-sm">Pay at Pickup</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Cash or card when you arrive
                        </p>
                      </label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* Cash at Pickup - Order Submission */}
              {deliveryMethod === 'local_pickup' && paymentMethod === 'cash' ? (
                <div data-testid="cash-payment-section">
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Banknote className="w-6 h-6 text-green-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-800">Pay at Pickup</p>
                        <p className="text-sm text-green-700 mt-1">
                          Your order will be reserved. Bring cash or card to pay when you pick up.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    type="button"
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-full font-semibold"
                    disabled={isProcessing || !selectedPickupDate || !selectedPickupTime}
                    onClick={handleCashAtPickupOrder}
                    data-testid="place-order-cash-btn"
                  >
                    {isProcessing ? 'Processing...' : `Place Order - Pay $${(total - (promoApplied?.discount_amount || 0)).toFixed(2)} at Pickup`}
                  </Button>
                </div>
              ) : (
                <>
                  <PaymentForm
                    applicationId={SQUARE_APP_ID}
                    locationId={SQUARE_LOCATION_ID}
                    cardTokenizeResponseReceived={(token, verifiedBuyer) => {
                      const formData = watch();
                      handleCardTokenization(token, formData);
                    }}
                    createPaymentRequest={() => ({
                      countryCode: 'US',
                      currencyCode: 'USD',
                      total: {
                        amount: (total - (promoApplied?.discount_amount || 0)).toFixed(2),
                        label: 'AquaPlants Plus',
                      },
                      requestShippingContact: deliveryMethod === 'shipping',
                      requestBillingContact: true,
                    })}
                  >
                    {/* Google Pay Button */}
                    <div className="mb-3">
                      <GooglePay />
                    </div>
                    
                    {/* Apple Pay Button */}
                    <div className="mb-3">
                      <ApplePay />
                    </div>
                    
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-muted-foreground">Or pay with card</span>
                      </div>
                    </div>
                    
                    <CreditCard
                      includeInputLabels={false}
                      buttonProps={{
                        css: {
                          backgroundColor: '#2d5016',
                          fontSize: '16px',
                          color: '#fff',
                          fontWeight: '600',
                          padding: '14px',
                          borderRadius: '9999px',
                          '&:hover': {
                            backgroundColor: '#3d6b1e',
                          },
                          '&:disabled': {
                            backgroundColor: '#9ca3af',
                            cursor: 'not-allowed',
                          },
                        },
                      }}
                      style={{
                        '.input-container': {
                          borderColor: '#e5e7eb',
                          borderRadius: '8px',
                        },
                        '.input-container.is-focus': {
                          borderColor: '#2d5016',
                        },
                        '.message-text': {
                          color: '#dc2626',
                        },
                        '.message-text.is-error': {
                          color: '#dc2626',
                        },
                      }}
                      render={(Button) => (
                        <Button disabled={isProcessing || (deliveryMethod === 'local_pickup' && (!selectedPickupDate || !selectedPickupTime))}>
                          {isProcessing ? 'Processing...' : `Pay $${(total - (promoApplied?.discount_amount || 0)).toFixed(2)}`}
                        </Button>
                      )}
                    />
                  </PaymentForm>
                </>
              )}
              
              <p className="text-xs text-center text-muted-foreground mt-4">
                🔒 Payments secured by Square. Your card details are encrypted and secure.
              </p>
            </div>
          </Card>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
